import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Tooltip as KTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { EyeOff, Stars02 as StarsIcon } from '@kula-ai/icons-core';
import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Link,
  MenuItem,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { API_SERVICE } from '@/api/candidate-job-slider';
import { ModalWithForm } from '@/components/ats/modal/ModalWithForm';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { CandidateSocialLinks } from '@/components/shared/contacts';
import {
  APPLICATION_QUERY_STALE_MS,
  getApplicationQueryKey,
} from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { ScheduleInterview } from '@/components/shared/interview-manager/ScheduleInterview';
import { useGetContactByIdQuery } from '@/store/query/contacts';
import { useDateFormat, useShowToast } from '@/hooks';
import { useCameFromPath } from '@/hooks/usePreviousLocation';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useDeleteInterviewMutation } from '@/store/query/scheduling';
import { type InterviewType } from '@/types/interview';
import {
  KDrawer,
  KMenuButton,
  KTagWithIcon,
  KText,
} from '@/ui-library/components';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { Stars02 } from '@kula-ai/icons/dual-tone/Stars02';
import { XClose } from '@kula-ai/icons/dual-tone/XClose';
import { SlashDivider } from '@kula-ai/icons/outline/SlashDivider';
import { UserCircle } from '@kula-ai/icons/outline/UserCircle';
import { Recording01 } from '@kula-ai/icons/solid/Recording01';

import { getInterviewTime, locationIconMap } from '../utils';
import { DemoInterviewModal } from './DemoInterviewModal';
import { InterviewIntelligenceStatusTag } from './InterviewIntelligenceStatusTag';
import { InterviewStatusTag } from './InterviewStatusTag';
import { JoinInterviewButton } from './JoinInterviewButton';
import { MeetingLinkDetails } from './MeetingLinkDetails';
import { useInterviewerAssistant } from './InterviewerAssistantProvider';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';

dayjs.extend(relativeTime);

// The candidate drawer pulls in the whole application surface (stage actions, editors,
// conversations). It only mounts once "View candidate" is clicked, so keep it out of the
// interview page's first-paint chunk.
const CandidateJobSliderV1 = lazy(() =>
  import('@/components/candidate-job-slider-v1').then((module) => ({
    default: module.CandidateJobSliderV1,
  }))
);

interface DisclosureActions {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

interface InterviewHeaderState {
  isBeforeInterview: boolean;
  isAfterInterview: boolean;
  isDuringInterview: boolean;
  interviewInfoActions: DisclosureActions;
  hideInterviewInfoInToolbar: boolean;
}

interface InterviewHeaderProps {
  interview: InterviewType;
  refetch: () => void;
  interviewState: InterviewHeaderState;
}

export const InterviewHeader = ({
  interview,
  refetch,
  interviewState,
}: InterviewHeaderProps) => {
  const history = useHistory();
  const toast = useShowToast();
  const cameFromInterviews = useCameFromPath('/interviews');
  const assistant = useInterviewerAssistant();
  const currUser = useSelector(selectCurrUser);
  const editInterviewActions = useDisclosure();
  const cancelInterviewActions = useDisclosure();
  const candidateSliderActions = useDisclosure();
  const [isHeaderHovered, setHeaderHovered] = useState(false);
  const [deleteInterview, { isLoading: isCancelling }] =
    useDeleteInterviewMutation();
  const [now, setNow] = useState(dayjs());

  const {
    meeting_link,
    candidate,
    intelligence_status,
    intelligence_status_reason,
    meeting_status,
    ats_job,
    ats_application_id,
    ats_job_stage_id,
    ats_job_stage_action_id = null,
    can_edit,
    start_time,
    end_time,
    ats_job_stage_name,
  } = interview;
  const {
    isBeforeInterview,
    isAfterInterview,
    isDuringInterview,
    interviewInfoActions,
    hideInterviewInfoInToolbar,
  } = interviewState;
  const isCancelledInterview = meeting_status === 'cancelled';
  const isNoteTaker = intelligence_status !== 'pending';
  const showCompactHeader = assistant.isHeaderCompact && !isHeaderHovered;

  // Contact data supplies the social links beside the candidate name. The endpoint remains the
  // permission boundary, so the query is gated only on the candidate id.
  const {
    data: contactDetails,
    isLoading: isContactLoading,
    refetch: refetchContactDetails,
  } = useGetContactByIdQuery(
    { contactId: candidate?.id },
    { skip: !candidate?.id }
  );

  const closeCandidateSlider = () => {
    candidateSliderActions.onClose();
    if (candidate?.id) refetchContactDetails();
  };

  useEffect(() => {
    const timer = window.setInterval(() => setNow(dayjs()), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  const applicationId =
    ats_application_id === null || ats_application_id === undefined
      ? null
      : String(ats_application_id);
  // This request also gates the in-place candidate drawer, so it must resolve before the
  // candidate affordance is shown. Its shared query key keeps the drawer on a warm cache.
  const { data: applicationData, isSuccess: isApplicationLoaded } = useQuery<
    Record<string, unknown>
  >({
    queryKey: getApplicationQueryKey(applicationId),
    queryFn: () =>
      API_SERVICE.getApplication(applicationId as string, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
    enabled: !!applicationId,
    refetchOnWindowFocus: false,
  });

  // The drawer evicts this query on close. Latch the endpoint's last authoritative answer so the
  // action does not disappear while the cache is repopulated.
  const candidateAccessRef = useRef<boolean | null>(null);
  if (isApplicationLoaded) {
    candidateAccessRef.current =
      !applicationData?.accessDenied && !applicationData?.applicationDeleted;
  }
  const canViewCandidate = candidateAccessRef.current === true;

  const handleCancelInterview = async () => {
    const result = (await deleteInterview({ interviewId: interview.id })) as {
      error?: unknown[];
    };
    if (result?.error?.length) {
      toast({ title: 'Cancelling the interview failed', status: 'error' });
      return;
    }

    toast({ title: 'The interview has been canceled.' });
    if (cameFromInterviews && history.length > 1) history.goBack();
    else history.push('/interviews/upcoming');
  };

  const menuItems = [
    !isCancelledInterview &&
      hideInterviewInfoInToolbar && {
        id: 'interview_info',
        label: 'Interview info',
        icon: InfoCircle,
        onClick: interviewInfoActions.onOpen,
      },
    !isCancelledInterview &&
      can_edit && {
        id: 'edit_interview',
        label: 'Edit interview',
        icon: Edit05,
        onClick: editInterviewActions.onOpen,
      },
    !isCancelledInterview &&
      can_edit && {
        id: 'cancel_interview',
        label: 'Cancel interview',
        icon: XClose,
        onClick: cancelInterviewActions.onOpen,
      },
    !isCancelledInterview &&
      assistant.demoHarnessAvailable && {
        id: 'run_demo_interview',
        label: 'Run demo interview',
        icon: Recording01,
        onClick: assistant.openDemoModal,
      },
  ].filter(Boolean) as Array<{
    id: string;
    label: string;
    icon?: React.ElementType;
    onClick: () => void;
  }>;

  const showJoinInterviewButton =
    meeting_link &&
    (isBeforeInterview || isDuringInterview) &&
    !isAfterInterview;

  const viewCandidateButton = canViewCandidate && (
    <Button variant="secondary" onClick={candidateSliderActions.onOpen}>
      <Icon as={UserCircle} width="16px" height="16px" mr="6px" />
      View candidate
    </Button>
  );

  const rightActions = (
    <Flex gap="12px" align="center">
      {viewCandidateButton}
      {showJoinInterviewButton && <JoinInterviewButton interview={interview} />}
      {menuItems.length > 0 && (
        <KMenuButton
          variant="tertiary"
          triggerContent="More"
          menuContent={menuItems.map((item) => (
            <MenuItem
              key={item.id}
              onClick={item.onClick}
              gap="8px"
              _hover={{ fontWeight: 'semibold', bg: 'secondary-50' }}
            >
              {item.icon && (
                <Icon as={item.icon} w="16px" h="16px" color="secondary-600" />
              )}
              <KText size="sm-regular" fontWeight="inherit">
                {item.label}
              </KText>
            </MenuItem>
          ))}
        />
      )}
    </Flex>
  );

  const identity = (
    <Flex align="center" gap="8px" minW={0}>
      <Avatar
        src={candidate.avatar ?? undefined}
        name={candidate.name}
        size={24}
        shape="square"
      />
      {canViewCandidate ? (
        <Link
          as="button"
          type="button"
          onClick={candidateSliderActions.onOpen}
          outline="none"
          _focus={{}}
        >
          <TruncatedTextWithTooltip
            text={candidate.name}
            variant="heading_1"
            maxW={showCompactHeader ? '220px' : '420px'}
          />
        </Link>
      ) : (
        <TruncatedTextWithTooltip
          text={candidate.name}
          variant="heading_1"
          maxW={showCompactHeader ? '220px' : '420px'}
        />
      )}
      <CandidateSocialLinks
        social={contactDetails?.social}
        ats={contactDetails?.ats}
        isLoading={isContactLoading}
        gap="6px"
      />
      {interview.transfer_info && (
        <TransferredBadge
          transferInfo={interview.transfer_info}
          showTooltipToLabel
        />
      )}
      <Icon as={SlashDivider} color="secondary-600" h="20px" />
      <TruncatedTextWithTooltip
        text={interview.name}
        variant="heading_1"
        maxW={showCompactHeader ? '260px' : '420px'}
      />
      {isNoteTaker && isBeforeInterview && (
        <KTagWithIcon
          value="AI notetaker enabled"
          icon={Stars02}
          iconProps={{ color: 'pastel-3-dark' }}
          bg="pastel-3-light"
          color="pastel-3-dark"
          whiteSpace="nowrap"
        />
      )}
      {!isBeforeInterview && isNoteTaker && (
        <InterviewIntelligenceStatusTag
          status={intelligence_status}
          reason={intelligence_status_reason}
        />
      )}
      <InterviewStatusTag interview={interview} />
      {isBeforeInterview &&
        !isCancelledInterview &&
        dayjs(start_time).diff(now, 'minutes') < 30 &&
        dayjs(start_time).diff(now, 'seconds') >= 3 && (
          <KTagWithIcon
            value={
              dayjs(start_time).diff(now, 'seconds') < 30
                ? 'Starts in a few seconds'
                : dayjs(start_time).diff(now, 'seconds') < 60
                  ? 'Starts in a minute'
                  : `Starts in ${Math.ceil(dayjs(start_time).diff(now, 'seconds') / 60)} minutes`
            }
            icon={Clock}
            whiteSpace="nowrap"
          />
        )}
    </Flex>
  );

  const { dateFormat, timeFormat } = useDateFormat();
  const interviewTimeResult = getInterviewTime({
    startTime: start_time,
    endTime: end_time,
    timeFormat,
    dateFormat: dateFormat.label,
    currUserTimezone: currUser?.time_zone,
  });
  const interviewTimeLabel =
    typeof interviewTimeResult === 'string'
      ? interviewTimeResult
      : interviewTimeResult.interviewTimeLabel;
  const videoDetails = Array.isArray(interview.meeting_meta)
    ? interview.meeting_meta.find((item) => item?.entry_point_type === 'video')
    : undefined;
  const locationIcon = locationIconMap[interview.location];

  const fullMetadata = (
    <Flex align="center" gap="12px" pt="8px">
      <Flex align="center" gap="6px">
        <Icon as={LayersThree02} color="secondary-500" />
        <Link href={`/jobs/${ats_job?.id}`} target="_blank" outline="none">
          <KText variant="paragraph" fontWeight="550">
            {ats_job?.name}
          </KText>
        </Link>
      </Flex>
      <KText color="secondary-500">•</KText>
      <Flex align="center" gap="6px">
        {locationIcon?.icon && (
          <Icon as={locationIcon.icon} color={locationIcon.color} />
        )}
        <KText
          textTransform="capitalize"
          size="sm-medium"
          color="secondary-700"
        >
          {interview.location?.replace('_', ' ')}
        </KText>
        {videoDetails && (
          <KPopover
            triggerElement={
              <Center>
                <Icon as={InfoCircle} cursor="pointer" />
              </Center>
            }
            popoverProps={{ trigger: 'hover' }}
            showCloseButton={false}
          >
            <MeetingLinkDetails
              videoDetails={videoDetails}
              location={interview.location}
            />
          </KPopover>
        )}
      </Flex>
      <KText color="secondary-500">•</KText>
      <Flex align="center" gap="6px">
        <Icon as={Clock} color="secondary-500" />
        <KText variant="paragraph" fontWeight="550">
          {interviewTimeLabel}
        </KText>
      </Flex>
    </Flex>
  );

  const assistantPrivacy = (
    <TooltipProvider delayDuration={150}>
      <KTooltip>
        <TooltipTrigger asChild>
          <Box as="span" display="inline-flex" color="secondary-600">
            <EyeOff size={18} />
          </Box>
        </TooltipTrigger>
        <TooltipContent side="top">Visible only to you</TooltipContent>
      </KTooltip>
    </TooltipProvider>
  );

  const viewTabs = assistant.showAssistant ? (
    <Flex h="38px" align="flex-end" gap="28px" mt="4px">
      <Flex
        as="button"
        align="center"
        h="38px"
        borderBottom="2px solid"
        borderColor={
          assistant.view === 'details' ? 'primary-500' : 'transparent'
        }
        color={assistant.view === 'details' ? 'primary-600' : 'secondary-700'}
        onClick={assistant.openDetails}
      >
        <KText size="sm-semi-bold" color="inherit">
          Candidate details
        </KText>
      </Flex>
      <Flex
        as="button"
        align="center"
        gap="8px"
        h="38px"
        borderBottom="2px solid"
        borderColor={
          assistant.view === 'assistant' ? 'primary-500' : 'transparent'
        }
        color={assistant.view === 'assistant' ? 'primary-600' : 'secondary-700'}
        onClick={() => assistant.openAssistant(assistant.defaultMode)}
      >
        <StarsIcon size={18} />
        <KText size="sm-semi-bold" color="inherit">
          Interviewer assistant
        </KText>
        {assistant.view === 'assistant' && assistantPrivacy}
      </Flex>
    </Flex>
  ) : null;

  return (
    <>
      <Flex
        as="header"
        px="24px"
        pt={showCompactHeader ? '8px' : '10px'}
        bg="base-white"
        borderBottom="1px solid"
        borderColor="secondary-100"
        flexDir="column"
        transition="padding 160ms ease"
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
      >
        {showCompactHeader ? (
          <Flex minH="36px" align="center" justify="space-between" gap="16px">
            <Flex align="center" gap="10px" minW={0}>
              <KText
                size="sm-regular"
                color="secondary-600"
                cursor="pointer"
                noOfLines={1}
                maxW="82px"
                onClick={() => history.push('/interviews/upcoming')}
              >
                Interviews /
              </KText>
              {identity}
            </Flex>
            {rightActions}
          </Flex>
        ) : (
          <>
            <Flex justify="space-between" align="flex-start" gap="16px">
              <Box minW={0}>
                <Flex align="center" gap="6px" mb="4px">
                  <Icon as={Recording01} color="secondary-600" />
                  <KText
                    size="sm-regular"
                    color="secondary-600"
                    cursor="pointer"
                    onClick={() => history.push('/interviews/upcoming')}
                  >
                    Interviews &amp; meetings /
                  </KText>
                </Flex>
                {identity}
              </Box>
              {rightActions}
            </Flex>
            {fullMetadata}
          </>
        )}
        {viewTabs}
      </Flex>

      {editInterviewActions.isOpen && (
        <KDrawer
          isOpen
          onClose={editInterviewActions.onClose}
          handleOutsideClose={() => {}}
          variant="full"
          contentProps={{ maxW: '90%' }}
          skipHeader
          skipFooter
        >
          <ScheduleInterview
            mode="manual"
            apiReference="edit"
            applicationId={ats_application_id}
            applicationData={applicationData}
            jobId={ats_job?.id}
            stageId={ats_job_stage_id}
            stageActivityId={ats_job_stage_action_id}
            interviewId={interview.id}
            candidateId={candidate?.id}
            candidateName={candidate?.name}
            candidateAvatar={candidate?.avatar ?? undefined}
            candidateEmail={candidate?.email}
            jobTitle={ats_job?.name}
            stageName={ats_job_stage_name}
            onClose={editInterviewActions.onClose}
            onSuccess={() => {
              refetch();
              editInterviewActions.onClose();
            }}
          />
        </KDrawer>
      )}
      {cancelInterviewActions.isOpen && (
        <ModalWithForm
          withForm={false}
          isOpen
          onClose={cancelInterviewActions.onClose}
          headerText="Cancel interview?"
          subHeaderText="This will remove the interview from the calendars of the candidate and interviewers"
          primaryCtaText="Cancel interview"
          handleSubmit={handleCancelInterview}
          isCtaLoading={isCancelling}
        />
      )}
      {candidateSliderActions.isOpen && (
        <Suspense
          fallback={
            <Center
              position="fixed"
              inset="0"
              bg="blackAlpha.300"
              zIndex="1400"
              data-test-id="candidate-drawer-loading"
            >
              <Spinner color="primary-800" />
            </Center>
          }
        >
          <CandidateJobSliderV1
            incomingApplicationId={String(ats_application_id)}
            onClose={closeCandidateSlider}
            navigateWithoutParam
          />
        </Suspense>
      )}
      {assistant.isDemoModalOpen && (
        <DemoInterviewModal
          isOpen
          onClose={assistant.closeDemoModal}
          onStart={assistant.startDemo}
          isStarting={assistant.isStartingDemo}
        />
      )}
    </>
  );
};
